// Shareable view state in the query string. Only values that differ from the
// curve's defaults are written. Camera is stored viewport-independently as
// the world point at the viewport centre plus the world span across the
// shorter viewport side.
import type { AppState, CameraState, Closure, ColorMode, Detail, GradientState, Manifest, StatePatch } from '../types.ts';
import { defaultsFor } from './defaults.ts';
import { GRADIENT_PRESETS } from '../gradient/presets.ts';
import { findCurve, findExport, defaultExport } from '../data/manifest.ts';
import { TILES_DEFAULT, VIEW3D_DEFAULT } from './defaults.ts';
import { quatFromEuler, quatNormalize } from '../render/camera.ts';
import type { Quat, TilesState, View3dState } from '../types.ts';
import { hexToRgb, rgbToHex } from '../gradient/ramp.ts';

const SHORT: Record<string, string> = {
  hatcurve: 'hat', penrosecurve: 'penrose', quaquacurve: 'quaqua', quaquaversalcurve: 'quaquaversal',
  spectrecurve: 'spectre', pinwheelcurve: 'pinwheel', hilbertcurve: 'hilbert',
};
const LONG: Record<string, string> = Object.fromEntries(Object.entries(SHORT).map(([k, v]) => [v, k]));

const MODE: Record<ColorMode, string> = { roygbiv: 'g', helix: 'h', solid: 's' };
const MODE_IN: Record<string, ColorMode> = { g: 'roygbiv', h: 'helix', s: 'solid' };
const CLOSURE: Record<Closure, string> = { mirror: 'm', wrap: 'w', none: 'n' };
const CLOSURE_IN: Record<string, Closure> = { m: 'mirror', w: 'wrap', n: 'none' };

const hex6 = (h: string) => rgbToHex(hexToRgb(h)).slice(1);
const num = (v: string | null, lo: number, hi: number): number | undefined => {
  if (v === null) return undefined;
  const n = parseFloat(v);
  return isFinite(n) ? Math.max(lo, Math.min(hi, n)) : undefined;
};
const trim = (n: number, dp: number) => {
  const s = n.toFixed(dp);
  return s.indexOf('.') >= 0 ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
};

export interface CameraView {
  cx: number;   // world x at viewport centre (post-stretch / post-rotation frame)
  cy: number;
  span: number; // world units across min(viewW, viewH)
  rot?: Quat;         // 3D only
}

export function cameraToView(cam: CameraState, viewW: number, viewH: number, is3d: boolean): CameraView {
  const v: CameraView = {
    cx: (viewW / 2 - cam.tx) / cam.scale,
    cy: -(viewH / 2 - cam.ty) / cam.scale,
    span: Math.min(viewW, viewH) / cam.scale,
  };
  if (is3d) v.rot = [cam.rot[0], cam.rot[1], cam.rot[2], cam.rot[3]];
  return v;
}

export function viewToCamera(v: CameraView, viewW: number, viewH: number): CameraState {
  const scale = Math.min(viewW, viewH) / Math.max(1e-12, v.span);
  return {
    scale,
    tx: viewW / 2 - v.cx * scale,
    ty: viewH / 2 + v.cy * scale,
    rot: v.rot ? quatNormalize(v.rot) : [0, 0, 0, 1],
  };
}

export function encodeView(v: CameraView): string {
  const dp = Math.max(0, Math.min(12, Math.ceil(-Math.log10(Math.max(1e-12, v.span))) + 4));
  const parts = [trim(v.cx, dp), trim(v.cy, dp), v.span.toPrecision(4).replace(/\.?0+$/, '')];
  if (v.rot) parts.push(...v.rot.map((x) => trim(x, 4)));
  return parts.join('_');
}

export function decodeView(s: string | null): CameraView | null {
  if (!s) return null;
  const p = s.split('_').map(parseFloat);
  if (p.length < 3 || p.some((x) => !isFinite(x)) || p[2] <= 0) return null;
  const v: CameraView = { cx: p[0], cy: p[1], span: p[2] };
  if (p.length >= 7) v.rot = quatNormalize([p[3], p[4], p[5], p[6]]);
  else if (p.length >= 5) v.rot = quatFromEuler(p[3], p[4]);   // links from before the arcball camera
  return v;
}

/** Encode the parts of state worth sharing. `view` is omitted when null (fit). */
export function encodeUrlState(s: AppState, view: CameraView | null): string {
  const q = new URLSearchParams();
  const d = defaultsFor(s.curve);
  const dStops = d.stops ?? GRADIENT_PRESETS[d.preset ?? 'roygbiv'];
  q.set('c', SHORT[s.curve] ?? s.curve);
  q.set('e', s.exportId);
  if (s.colorMode !== (d.colorMode ?? 'roygbiv')) q.set('m', MODE[s.colorMode]);
  const g = s.grad;
  const isPreset = g.preset !== 'custom' && GRADIENT_PRESETS[g.preset] && GRADIENT_PRESETS[g.preset].join() === g.stops.join();
  if (isPreset) { if (g.preset !== (d.preset ?? 'roygbiv')) q.set('p', g.preset); }
  else if (g.stops.join() !== dStops.join()) q.set('s', g.stops.map(hex6).join('-'));
  if (g.repeat !== (d.repeat ?? 1) && !g.autoCycles) q.set('r', String(g.repeat));
  if (g.pingpong !== (d.pingpong ?? true)) q.set('pp', g.pingpong ? '1' : '0');
  if (g.reverse !== !!d.reverse) q.set('rv', g.reverse ? '1' : '0');
  if (g.hard !== !!d.hard) q.set('hd', g.hard ? '1' : '0');
  if (g.autoCycles !== !!d.autoCycles) q.set('ac', g.autoCycles ? '1' : '0');
  if (g.closure !== (d.closure ?? 'mirror')) q.set('cl', CLOSURE[g.closure]);
  if (Math.abs(g.rotation - (d.rotation ?? 0)) > 5e-4) q.set('rot', trim(g.rotation, 3));
  if (Math.abs(s.aspect - (d.aspect ?? 0)) > 5e-3) q.set('a', trim(s.aspect, 2));
  if (Math.abs(s.width - (d.width ?? 1.2)) > 0.05) q.set('w', trim(s.width, 1));
  if (hex6(s.bgHex) !== hex6(d.bgHex ?? '#ffffff')) q.set('bg', hex6(s.bgHex));
  if (hex6(s.solidHex) !== hex6(d.solidHex ?? '#000000')) q.set('fg', hex6(s.solidHex));
  // overlays and 3D options (defaults omitted)
  if (s.tiles.show) q.set('t', '1');
  if (!s.tiles.highlight) q.set('th', '0');
  if (Math.abs(s.tiles.opacity - TILES_DEFAULT.opacity) > 1e-6) q.set('to', String(Math.round(s.tiles.opacity * 100) / 100));
  if (s.view3d.projection !== VIEW3D_DEFAULT.projection) q.set('pj', 'o');
  if (s.view3d.xray) q.set('xr', '1');
  if (s.view3d.cage) q.set('cg', '1');
  if (s.view3d.opacity < 1) q.set('op', String(Math.round(s.view3d.opacity * 100) / 100));
  if (s.endpoints) q.set('ep', '1');
  if (s.plate) q.set('pl', s.plate);
  if (view) q.set('v', encodeView(view));
  return q.toString();
}

export interface DecodedUrl {
  patch: StatePatch;
  curve: string | null;
  exportId: string | null;
  view: CameraView | null;
  detail?: Detail;
  tiles?: Partial<TilesState>;
  view3d?: Partial<View3dState>;
  endpoints?: boolean;
}

/** Parse a query string into a state patch. Unknown or malformed values are ignored. */
export function decodeUrlState(search: string, manifest: Manifest): DecodedUrl {
  const q = new URLSearchParams(search);
  const out: DecodedUrl = { patch: {}, curve: null, exportId: null, view: null };
  const c = q.get('c');
  if (c) {
    const id = findCurve(manifest, c) ? c : LONG[c] && findCurve(manifest, LONG[c]) ? LONG[c] : null;
    if (id) out.curve = id;
  }
  const curve = out.curve ? findCurve(manifest, out.curve)! : manifest.curves[0];
  const e = q.get('e');
  if (e) {
    if (findExport(curve, e)) out.exportId = e;
    else {
      const m = /^d(\d+)$/.exec(e);
      const byDepth = m ? curve.exports.find((x) => x.depth === parseInt(m[1], 10)) : undefined;
      out.exportId = byDepth ? byDepth.id : defaultExport(curve).id;
    }
  }
  const P: StatePatch = {};
  const m = q.get('m'); if (m && MODE_IN[m]) P.colorMode = MODE_IN[m];
  const s = q.get('s');
  if (s) {
    const stops = s.split('-').filter((x) => /^[0-9a-fA-F]{3,6}$/.test(x)).map((x) => '#' + hex6(x));
    if (stops.length >= 2) P.stops = stops;
  }
  const p = q.get('p'); if (!P.stops && p && GRADIENT_PRESETS[p]) P.preset = p;
  const r = num(q.get('r'), 1, 64); if (r !== undefined) P.repeat = Math.round(r);
  const pp = q.get('pp'); if (pp !== null) P.pingpong = pp !== '0';
  const rv = q.get('rv'); if (rv !== null) P.reverse = rv === '1';
  const hd = q.get('hd'); if (hd !== null) P.hard = hd === '1';
  const ac = q.get('ac'); if (ac !== null) P.autoCycles = ac === '1';
  const cl = q.get('cl'); if (cl && CLOSURE_IN[cl]) P.closure = CLOSURE_IN[cl];
  const rot = num(q.get('rot'), 0, 1); if (rot !== undefined) P.rotation = rot;
  const a = num(q.get('a'), -1.6, 1.6); if (a !== undefined) P.aspect = a;
  const w = num(q.get('w'), 0.2, 8); if (w !== undefined) P.width = w;
  const bg = q.get('bg'); if (bg && /^[0-9a-fA-F]{3,6}$/.test(bg)) P.bgHex = '#' + hex6(bg);
  const fg = q.get('fg'); if (fg && /^[0-9a-fA-F]{3,6}$/.test(fg)) P.solidHex = '#' + hex6(fg);
  const pl = q.get('pl'); if (pl) P.plate = pl;
  out.patch = P;
  out.view = decodeView(q.get('v'));
  const tiles: Partial<TilesState> = {};
  if (q.get('t') === '1') tiles.show = true;
  if (q.get('th') === '0') tiles.highlight = false;
  const to = num(q.get('to'), 0.02, 0.4); if (to !== undefined) tiles.opacity = to;
  if (Object.keys(tiles).length) out.tiles = tiles;
  const v3: Partial<View3dState> = {};
  if (q.get('pj') === 'o') v3.projection = 'ortho';
  if (q.get('xr') === '1') v3.xray = true;
  if (q.get('cg') === '1') v3.cage = true;
  const op = num(q.get('op'), 0.05, 1); if (op !== undefined) v3.opacity = op;
  if (Object.keys(v3).length) out.view3d = v3;
  if (q.get('ep') === '1') out.endpoints = true;
  return out;
}

/** Apply a decoded patch on top of a full state (used at boot). */
export function applyPatchToState(base: AppState, d: DecodedUrl, manifest?: Manifest): AppState {
  const P = d.patch;
  const grad: GradientState = { ...base.grad };
  if (P.stops) { grad.stops = P.stops.slice(); grad.preset = 'custom'; }
  else if (P.preset) { grad.preset = P.preset; grad.stops = GRADIENT_PRESETS[P.preset].slice(); }
  if (P.repeat !== undefined) grad.repeat = P.repeat;
  if (P.pingpong !== undefined) grad.pingpong = P.pingpong;
  if (P.reverse !== undefined) grad.reverse = P.reverse;
  if (P.hard !== undefined) grad.hard = P.hard;
  if (P.autoCycles !== undefined) grad.autoCycles = P.autoCycles;
  if (P.closure) grad.closure = P.closure;
  if (P.rotation !== undefined) grad.rotation = P.rotation;
  return {
    ...base,
    curve: d.curve ?? base.curve,
    // A link naming only the curve opens it at its default iteration, not at the previous curve's export.
    exportId: d.exportId ?? (manifest && d.curve && d.curve !== base.curve ? defaultExport(findCurve(manifest, d.curve)!).id : base.exportId),
    grad,
    colorMode: P.colorMode ?? base.colorMode,
    aspect: P.aspect ?? base.aspect,
    width: P.width ?? base.width,
    bgHex: P.bgHex ?? base.bgHex,
    solidHex: P.solidHex ?? base.solidHex,
    plate: P.plate ?? base.plate,
    tiles: { ...base.tiles, ...(d.tiles ?? {}) },
    view3d: { ...base.view3d, ...(d.view3d ?? {}) },
    endpoints: d.endpoints ?? base.endpoints,
  };
}
